import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminDashboardLayout from './AdminDashboardLayout';
import WebRTCTest from './WebRTCTest';
import AdminPaymentManagement from './AdminPaymentManagement';
import AdminSettings from './AdminSettings';
import AdminLogs from './AdminLogs';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
}

interface CallRecording {
  id: string;
  call_id: string;
  session_id: string;
  vartotojo_id: string;
  audio_data: string;
  mp3_audio_data?: string;
  format: string;
  duration_seconds?: number;
  size_bytes?: number;
  transcript?: string;
  ai_summary?: {
    trumpa_santrauka?: string;
    kliento_poreikis?: string;
    pasiulyti_sprendimai?: string[];
    aptartos_paslaugos?: string[];
    kaina_saskaita?: string;
    sutarti_veiksmai?: string[];
    sekantis_zingsnis?: string;
    pokalbio_nuotaika?: string;
    svarbus_klausimas?: string;
    has_important_info?: boolean;
  };
  recorded_at: string;
  user_name?: string;
  user_email?: string;
  user_profession?: string;
}

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalMinutes: 0,
    totalRevenue: 0,
    newRegistrations: 0
  });
  const [users, setUsers] = useState<any[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedProfession, setSelectedProfession] = useState<string>('');
  const [testCallStatus, setTestCallStatus] = useState<string>('');
  const [recordings, setRecordings] = useState<CallRecording[]>([]);
  const [recordingsLoading, setRecordingsLoading] = useState(false);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [recordingsFilter, setRecordingsFilter] = useState({
    userId: '',
    dateFrom: '',
    dateTo: ''
  });
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserData, setNewUserData] = useState({
    vardas: '',
    el_pastas: '',
    telefonas: '',
    profesijos_tipas: '',
    darbo_regionas: ''
  });
  const [requests, setRequests] = useState<any[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsFilter, setRequestsFilter] = useState({
    status: '',
    priority: ''
  });
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [helpChatConversations, setHelpChatConversations] = useState<any[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Sync activeTab with URL path
  useEffect(() => {
    const path = location.pathname;
    if (path === '/admin/dashboard' || path === '/admin/dashboard/') {
      setActiveTab('overview');
    } else if (path.startsWith('/admin/dashboard/users')) {
      setActiveTab('users');
    } else if (path.startsWith('/admin/dashboard/analytics')) {
      setActiveTab('analytics');
    } else if (path.startsWith('/admin/dashboard/payments')) {
      setActiveTab('payments');
    } else if (path.startsWith('/admin/dashboard/recordings')) {
      setActiveTab('recordings');
    } else if (path.startsWith('/admin/dashboard/requests')) {
      setActiveTab('requests');
    } else if (path.startsWith('/admin/dashboard/help-chats')) {
      setActiveTab('help-chats');
    } else if (path.startsWith('/admin/dashboard/test-voice')) {
      setActiveTab('test-voice');
    } else if (path.startsWith('/admin/dashboard/settings')) {
      setActiveTab('settings');
    } else if (path.startsWith('/admin/dashboard/logs')) {
      setActiveTab('logs');
    }
  }, [location]);

  useEffect(() => {
    // Update time every minute
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Check authentication
    fetch('/api/auth/status', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.authenticated && data.user?.isAdmin) {
          setAdminUser(data.user);
        } else {
          navigate('/admin/login');
        }
      })
      .catch(() => {
        navigate('/admin/login');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate]);

  // Standalone function to fetch users
  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/admin/users', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        console.error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchHelpChatConversations = async () => {
    setConversationsLoading(true);
    try {
      const response = await fetch('/api/admin/help-chat-conversations', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setHelpChatConversations(data.conversations || []);
      } else {
        console.error('Failed to fetch help chat conversations');
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  };

  // View user's raw prompt
  const viewUserPrompt = async (userId: string, profession: string) => {
    try {
      // Fetch the raw prompt from database
      const response = await fetch(`/api/admin/prompt/raw/${userId}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        alert('Klaida gaunant promptą');
        return;
      }

      const data = await response.json();

      // Open in new window for viewing
      const promptWindow = window.open('', '_blank', 'width=1400,height=900');
      if (promptWindow) {
        promptWindow.document.write(`
          <html>
            <head>
              <title>RAW AI Prompt - ${data.userData?.vardas || 'User'}</title>
              <style>
                body {
                  font-family: 'Courier New', monospace;
                  padding: 20px;
                  background: #1e1e1e;
                  color: #d4d4d4;
                  margin: 0;
                }
                h1, h2 { color: #4fc3f7; }
                h3 { color: #81c784; }
                .container { max-width: 1200px; margin: 0 auto; }
                .section {
                  background: #2d2d30;
                  padding: 20px;
                  margin: 20px 0;
                  border-radius: 8px;
                  border: 1px solid #3e3e42;
                }
                .metadata {
                  display: grid;
                  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                  gap: 20px;
                  margin: 20px 0;
                }
                .meta-item {
                  background: #252526;
                  padding: 15px;
                  border-radius: 5px;
                }
                .label {
                  color: #9cdcfe;
                  font-weight: bold;
                  display: inline-block;
                  margin-right: 10px;
                }
                .value { color: #ce9178; }
                .prompt-content {
                  background: #1e1e1e;
                  padding: 20px;
                  border-radius: 5px;
                  white-space: pre-wrap;
                  font-size: 14px;
                  line-height: 1.6;
                  border: 1px solid #464647;
                  max-height: 600px;
                  overflow-y: auto;
                }
                .template-content {
                  background: #1a1a2e;
                  padding: 20px;
                  border-radius: 5px;
                  white-space: pre-wrap;
                  font-size: 13px;
                  line-height: 1.5;
                  border: 1px solid #16213e;
                  max-height: 400px;
                  overflow-y: auto;
                  color: #b4b4b4;
                }
                .json-content {
                  background: #1e1e1e;
                  padding: 15px;
                  border-radius: 5px;
                  font-size: 12px;
                  overflow-x: auto;
                  border: 1px solid #464647;
                }
                .services {
                  background: #2d3436;
                  padding: 15px;
                  border-radius: 5px;
                  margin: 10px 0;
                }
                .service-item {
                  background: #1e1e1e;
                  padding: 10px;
                  margin: 5px 0;
                  border-radius: 3px;
                  border-left: 3px solid #4fc3f7;
                }
                .warning {
                  background: #5a4f00;
                  color: #ffd700;
                  padding: 10px;
                  border-radius: 5px;
                  margin: 10px 0;
                  border: 1px solid #8b7500;
                }
              </style>
            </head>
            <body>
              <div class="container">
                <h1>🔍 RAW AI Prompt Viewer</h1>

                ${data.hasPrompt ? `
                  <div class="section">
                    <h2>📋 Prompt Information</h2>
                    <div class="metadata">
                      <div class="meta-item">
                        <span class="label">User ID:</span>
                        <span class="value">${data.userData?.id || userId}</span>
                      </div>
                      <div class="meta-item">
                        <span class="label">Vardas:</span>
                        <span class="value">${data.userData?.vardas || 'N/A'}</span>
                      </div>
                      <div class="meta-item">
                        <span class="label">Profesija:</span>
                        <span class="value">${data.userData?.profesijos_tipas || profession}</span>
                      </div>
                      <div class="meta-item">
                        <span class="label">Version:</span>
                        <span class="value">${data.version || '1'}</span>
                      </div>
                      <div class="meta-item">
                        <span class="label">Created:</span>
                        <span class="value">${data.created ? new Date(data.created).toLocaleString('lt-LT') : 'N/A'}</span>
                      </div>
                      <div class="meta-item">
                        <span class="label">Updated:</span>
                        <span class="value">${data.updated ? new Date(data.updated).toLocaleString('lt-LT') : 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  ${data.userData?.custom_paslaugos ? `
                    <div class="section">
                      <h2>🛠️ Custom Services</h2>
                      <div class="services">
                        ${(() => {
                          const services = typeof data.userData.custom_paslaugos === 'string'
                            ? JSON.parse(data.userData.custom_paslaugos)
                            : data.userData.custom_paslaugos;
                          return services.map(s => `
                            <div class="service-item">
                              <strong>${s.paslauga}</strong>: €${s.kaina}/${s.vienetas}
                            </div>
                          `).join('');
                        })()}
                      </div>
                    </div>
                  ` : ''}

                  <div class="section">
                    <h2>📝 RAW Generated Prompt (Personalized)</h2>
                    <div class="prompt-content">${data.generatedPrompt || 'No generated prompt found'}</div>
                  </div>

                  <div class="section">
                    <h2>🎯 Template Used (Before Personalization)</h2>
                    <div class="template-content">${data.template || 'No template found'}</div>
                  </div>

                  <div class="section">
                    <h2>🔄 Replacements Applied</h2>
                    <div class="json-content">
                      <pre>${JSON.stringify(data.replacements || {}, null, 2)}</pre>
                    </div>
                  </div>

                  <div class="section">
                    <h2>💾 Full JSONB Data</h2>
                    <div class="json-content">
                      <pre>${JSON.stringify(data.fullData || {}, null, 2)}</pre>
                    </div>
                  </div>
                ` : `
                  <div class="warning">
                    ⚠️ No prompt found for this user. Generate one first using "Balso testas" or wait for user to complete registration.
                  </div>
                `}
              </div>
            </body>
          </html>
        `);
        promptWindow.document.close();
      }
    } catch (error) {
      console.error('Error viewing prompt:', error);
      alert('Klaida peržiūrint promptą');
    }
  };

  // Edit user's prompt
  const editUserPrompt = async (userId: string, profession: string) => {
    try {
      // First fetch the current prompt
      const response = await fetch(`/api/admin/prompt/raw/${userId}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        alert('Klaida gaunant promptą');
        return;
      }

      const data = await response.json();

      // Open edit window
      const editWindow = window.open('', '_blank', 'width=1400,height=900');
      if (editWindow) {
        editWindow.document.write(`
          <html>
            <head>
              <title>Edit AI Prompt - ${data.userData?.vardas || 'User'}</title>
              <style>
                body {
                  font-family: Arial, sans-serif;
                  padding: 20px;
                  background: #f5f5f5;
                  margin: 0;
                }
                h1 { color: #000042; }
                .container { max-width: 1200px; margin: 0 auto; }
                .form-group {
                  margin: 20px 0;
                }
                label {
                  display: block;
                  font-weight: bold;
                  margin-bottom: 10px;
                  color: #333;
                }
                textarea {
                  width: 100%;
                  padding: 15px;
                  border: 2px solid #ddd;
                  border-radius: 5px;
                  font-family: 'Courier New', monospace;
                  font-size: 14px;
                  line-height: 1.5;
                  background: white;
                  resize: vertical;
                }
                textarea:focus {
                  outline: none;
                  border-color: #000042;
                }
                .buttons {
                  display: flex;
                  gap: 10px;
                  margin-top: 20px;
                }
                button {
                  padding: 12px 24px;
                  border: none;
                  border-radius: 5px;
                  font-size: 16px;
                  cursor: pointer;
                  font-weight: bold;
                }
                .save-btn {
                  background: #28a745;
                  color: white;
                }
                .save-btn:hover {
                  background: #218838;
                }
                .cancel-btn {
                  background: #6c757d;
                  color: white;
                }
                .cancel-btn:hover {
                  background: #5a6268;
                }
                .reset-btn {
                  background: #ffc107;
                  color: #212529;
                }
                .reset-btn:hover {
                  background: #e0a800;
                }
                .info {
                  background: #d1ecf1;
                  border: 1px solid #bee5eb;
                  color: #0c5460;
                  padding: 15px;
                  border-radius: 5px;
                  margin: 20px 0;
                }
                .success {
                  background: #d4edda;
                  border: 1px solid #c3e6cb;
                  color: #155724;
                  padding: 15px;
                  border-radius: 5px;
                  margin: 20px 0;
                  display: none;
                }
                .error {
                  background: #f8d7da;
                  border: 1px solid #f5c6cb;
                  color: #721c24;
                  padding: 15px;
                  border-radius: 5px;
                  margin: 20px 0;
                  display: none;
                }
              </style>
            </head>
            <body>
              <div class="container">
                <h1>✏️ Edit AI Prompt</h1>

                <div class="info">
                  <strong>User:</strong> ${data.userData?.vardas || 'Unknown'}<br>
                  <strong>Profession:</strong> ${data.userData?.profesijos_tipas || profession}<br>
                  <strong>User ID:</strong> ${userId}
                </div>

                <div class="success" id="success-msg">
                  ✅ Prompt updated successfully!
                </div>

                <div class="error" id="error-msg">
                  ❌ Error updating prompt: <span id="error-text"></span>
                </div>

                <div class="form-group">
                  <label for="prompt-text">AI Prompt Content:</label>
                  <textarea
                    id="prompt-text"
                    rows="25"
                    placeholder="Enter the AI prompt here..."
                  >${data.generatedPrompt || data.template || ''}</textarea>
                </div>

                <div class="buttons">
                  <button class="save-btn" onclick="savePrompt()">💾 Save Changes</button>
                  <button class="reset-btn" onclick="resetPrompt()">🔄 Reset to Original</button>
                  <button class="cancel-btn" onclick="window.close()">❌ Cancel</button>
                </div>
              </div>

              <script>
                const originalPrompt = ${JSON.stringify(data.generatedPrompt || data.template || '')};
                const userId = ${JSON.stringify(userId)};
                const profession = ${JSON.stringify(profession)};

                function resetPrompt() {
                  if (confirm('Reset to original prompt?')) {
                    document.getElementById('prompt-text').value = originalPrompt;
                  }
                }

                async function savePrompt() {
                  const promptText = document.getElementById('prompt-text').value;

                  if (!promptText.trim()) {
                    alert('Prompt cannot be empty!');
                    return;
                  }

                  try {
                    const response = await fetch('/api/admin/prompt/update', {
                      method: 'POST',
                      credentials: 'include',
                      headers: {
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({
                        userId: userId,
                        profession: profession,
                        prompt: promptText
                      })
                    });

                    const result = await response.json();

                    if (result.success) {
                      document.getElementById('success-msg').style.display = 'block';
                      document.getElementById('error-msg').style.display = 'none';
                      setTimeout(() => {
                        document.getElementById('success-msg').style.display = 'none';
                      }, 3000);
                    } else {
                      document.getElementById('error-text').textContent = result.message || 'Unknown error';
                      document.getElementById('error-msg').style.display = 'block';
                      document.getElementById('success-msg').style.display = 'none';
                    }
                  } catch (error) {
                    document.getElementById('error-text').textContent = error.message;
                    document.getElementById('error-msg').style.display = 'block';
                    document.getElementById('success-msg').style.display = 'none';
                  }
                }
              </script>
            </body>
          </html>
        `);
        editWindow.document.close();
      }
    } catch (error) {
      console.error('Error editing prompt:', error);
      alert('Klaida redaguojant promptą');
    }
  };

  useEffect(() => {
    // Fetch real data from database
    const fetchAdminData = async () => {
      try {
        const [statsRes, usersRes, activityRes] = await Promise.all([
          fetch('/api/admin/stats', { credentials: 'include' }),
          fetch('/api/admin/users', { credentials: 'include' }),
          fetch('/api/admin/activity', { credentials: 'include' })
        ]);

        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }

        if (usersRes.ok) {
          const usersData = await usersRes.json();
          console.log('Users API response:', usersData);
          setUsers(usersData.users || []);
        } else {
          console.error('Failed to fetch users:', usersRes.status, usersRes.statusText);
          const errorText = await usersRes.text();
          console.error('Error response:', errorText);
        }

        if (activityRes.ok) {
          const activityData = await activityRes.json();
          setRecentActivity(activityData.activity || []);
        }
      } catch (error) {
        console.error('Error fetching admin data:', error);
      }
    };

    console.log('Admin user state:', adminUser);
    if (adminUser) {
      fetchAdminData();
    } else {
      console.log('No admin user, skipping data fetch');
    }
  }, [adminUser]);

  // Fetch analytics data when analytics tab is opened
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (activeTab === 'analytics' && !analyticsData && !analyticsLoading) {
        try {
          setAnalyticsLoading(true);
          const response = await fetch('/api/admin/analytics', { credentials: 'include' });
          if (response.ok) {
            const data = await response.json();
            // Transform analytics data to expected format
            // Process sessions by date for monthly chart
            const sessionsData = data.analytics?.sessions_by_date || [];
            const maxSessions = Math.max(...sessionsData.map((d: any) => parseInt(d.count)), 1);
            const monthlyChartData = sessionsData.slice(0, 7).map((item: any) => ({
              month: new Date(item.date).toLocaleDateString('lt-LT', { month: 'short', day: 'numeric' }),
              value: parseInt(item.count),
              height: `${(parseInt(item.count) / maxSessions) * 200}px`
            }));

            // Fill with empty data if less than 7 days
            while (monthlyChartData.length < 7) {
              monthlyChartData.push({
                month: '-',
                value: 0,
                height: '0px'
              });
            }

            const transformedData = {
              sessionData: {
                hasData: data.analytics?.sessions_by_date?.length > 0 || false,
                sessions: data.analytics?.sessions_by_date || []
              },
              metrics: [
                {
                  label: 'Viso vartotojų',
                  value: data.analytics?.overview?.total_users || 0,
                  trend: '+0%'
                },
                {
                  label: 'Aktyvūs vartotojai',
                  value: data.analytics?.overview?.active_users || 0,
                  trend: '+0%'
                },
                {
                  label: 'Viso sesijų',
                  value: data.analytics?.overview?.total_sessions || 0,
                  trend: '+0%'
                },
                {
                  label: 'Viso pokalbių',
                  value: data.analytics?.overview?.total_transcripts || 0,
                  trend: '+0%'
                }
              ],
              monthlyChart: monthlyChartData,
              professionDistribution: data.analytics?.users_by_profession || [],
              recentActivity: data.analytics?.recent_activity || []
            };
            setAnalyticsData(transformedData);
          } else {
            console.error('Failed to fetch analytics data');
          }
        } catch (error) {
          console.error('Error fetching analytics:', error);
        } finally {
          setAnalyticsLoading(false);
        }
      }
    };

    fetchAnalytics();
  }, [activeTab, analyticsData, analyticsLoading]);

  useEffect(() => {
    if (activeTab === 'help-chats' && helpChatConversations.length === 0 && !conversationsLoading) {
      fetchHelpChatConversations();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'recordings' && recordings.length === 0 && !recordingsLoading) {
      fetchRecordings();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'requests' && !requestsLoading) {
      fetchRequests();
    }
  }, [activeTab]);

  const fetchRecordings = async () => {
    try {
      setRecordingsLoading(true);
      const queryParams = new URLSearchParams();

      if (recordingsFilter.userId) {
        queryParams.append('userId', recordingsFilter.userId);
      }
      if (recordingsFilter.dateFrom) {
        queryParams.append('dateFrom', recordingsFilter.dateFrom);
      }
      if (recordingsFilter.dateTo) {
        queryParams.append('dateTo', recordingsFilter.dateTo);
      }

      const response = await fetch(`/api/admin/recordings?${queryParams}`, {
        credentials: 'include'
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        console.log('Admin recordings response:', data);
        setRecordings(data.recordings || []);
      } else {
        console.error('Error fetching recordings');
      }
    } catch (error) {
      console.error('Error fetching recordings:', error);
    } finally {
      setRecordingsLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      setRequestsLoading(true);
      const queryParams = new URLSearchParams();

      if (requestsFilter.status) {
        queryParams.append('status', requestsFilter.status);
      }
      if (requestsFilter.priority) {
        queryParams.append('priority', requestsFilter.priority);
      }

      const response = await fetch(`/api/admin/requests?${queryParams}`, {
        credentials: 'include'
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        console.log('Admin requests response:', data);
        setRequests(data.requests || []);
      } else {
        console.error('Error fetching requests');
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setRequestsLoading(false);
    }
  };

  const handleUpdateRequestStatus = async (requestId: string, updates: any) => {
    try {
      const response = await fetch(`/api/admin/requests/${requestId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        // Refresh requests list
        fetchRequests();
        setShowRequestModal(false);
        alert('Prašymas atnaujintas sėkmingai!');
      } else {
        alert('Nepavyko atnaujinti prašymo');
      }
    } catch (error) {
      console.error('Error updating request:', error);
      alert('Klaida atnaujinant prašymą');
    }
  };

  const playAudio = async (recordingId: string) => {
    try {
      if (playingAudio === recordingId) {
        // Stop current audio
        const audioElements = document.querySelectorAll('audio');
        audioElements.forEach(audio => {
          audio.pause();
          audio.currentTime = 0;
        });
        setPlayingAudio(null);
        return;
      }

      const response = await fetch(`/api/admin/recordings/${recordingId}/audio`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.audio_data) {
          // Stop any currently playing audio
          const audioElements = document.querySelectorAll('audio');
          audioElements.forEach(audio => {
            audio.pause();
            audio.currentTime = 0;
          });

          // Create new audio element
          const audio = new Audio();

          // Handle different formats
          let audioData = data.audio_data;
          let mimeType = 'audio/webm';

          if (data.mp3_audio_data) {
            audioData = data.mp3_audio_data;
            mimeType = 'audio/mpeg';
          }

          // Create blob URL
          const byteCharacters = atob(audioData);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: mimeType });
          const audioUrl = URL.createObjectURL(blob);

          audio.src = audioUrl;
          audio.play();
          setPlayingAudio(recordingId);

          audio.onended = () => {
            setPlayingAudio(null);
            URL.revokeObjectURL(audioUrl);
          };

          audio.onerror = () => {
            setPlayingAudio(null);
            URL.revokeObjectURL(audioUrl);
            alert('Klaida atkuriant garso įrašą');
          };
        }
      } else {
        alert('Klaida kraunant garso įrašą');
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      alert('Klaida atkuriant garso įrašą');
      setPlayingAudio(null);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          fontSize: '1.2rem',
          color: '#757575'
        }}>
          Kraunama...
        </div>
      </div>
    );
  }

  if (!adminUser) {
    return null; // Will redirect to login
  }

  return (
    <AdminDashboardLayout adminName={adminUser?.name}>
      <div style={{
        margin: '0 auto',
        padding: '0 1rem',
        width: '100%',
        boxSizing: 'border-box',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
      }}>
        {/* Time Info Header */}
        <div style={{
          background: '#ffffff',
          borderRadius: '25px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          padding: 'clamp(1rem, 3vw, 2rem)',
          marginBottom: '2rem',
          display: 'flex',
          flexDirection: window.innerWidth < 768 ? 'column' : 'row',
          gap: '1rem',
          justifyContent: 'space-between',
          alignItems: window.innerWidth < 768 ? 'flex-start' : 'center',
          boxSizing: 'border-box'
        }}>
          <div>
            <h2 style={{ color: '#000042', fontSize: '1.5rem', marginBottom: '0.2rem', fontFamily: 'Georgia, serif' }}>
              Valdymo panelė
            </h2>
            <span style={{ color: '#666666', fontSize: '0.9rem', fontWeight: 'normal' }}>
              {adminUser?.email}
            </span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#000042', fontWeight: '600' }}>
              {currentTime.toLocaleString('lt-LT', {
                timeZone: 'Europe/Vilnius',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        </div>


        {activeTab === 'overview' && (
          <div>
            {/* Page Title with Serif Font */}
            <h1 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(2rem, 5vw, 2.5rem)',
              fontWeight: 600,
              color: '#000042',
              marginBottom: '2rem',
              letterSpacing: '-0.02em'
            }}>
              Administratoriaus Pultas
            </h1>

            {/* Enhanced Platform Stats Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: '#fff',
                border: '1px solid #E5E5E5',
                borderRadius: '8px',
                padding: '1.5rem',
                transition: 'all 0.3s ease'
              }}>
                <div style={{
                  fontSize: '0.85rem',
                  color: '#666',
                  marginBottom: '0.5rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 500
                }}>
                  Viso Vartotojų
                </div>
                <div style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '2.5rem',
                  fontWeight: 600,
                  color: '#0066ff',
                  marginBottom: '0.25rem'
                }}>
                  {stats.totalUsers}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: 500 }}>
                  +{stats.newRegistrations} šį mėnesį
                </div>
              </div>

              <div style={{
                background: '#fff',
                border: '1px solid #E5E5E5',
                borderRadius: '8px',
                padding: '1.5rem',
                transition: 'all 0.3s ease'
              }}>
                <div style={{
                  fontSize: '0.85rem',
                  color: '#666',
                  marginBottom: '0.5rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 500
                }}>
                  Aktyvūs Vartotojai
                </div>
                <div style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '2.5rem',
                  fontWeight: 600,
                  color: '#10b981',
                  marginBottom: '0.25rem'
                }}>
                  {stats.activeUsers}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#999' }}>
                  {stats.totalUsers > 0 ? Math.round((stats.activeUsers / stats.totalUsers) * 100) : 0}% aktyvumas
                </div>
              </div>

              <div style={{
                background: '#fff',
                border: '1px solid #E5E5E5',
                borderRadius: '8px',
                padding: '1.5rem',
                transition: 'all 0.3s ease'
              }}>
                <div style={{
                  fontSize: '0.85rem',
                  color: '#666',
                  marginBottom: '0.5rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 500
                }}>
                  Bendros Minutės
                </div>
                <div style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '2.5rem',
                  fontWeight: 600,
                  color: '#000042',
                  marginBottom: '0.25rem'
                }}>
                  {stats.totalMinutes.toLocaleString()}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#999' }}>šį mėnesį</div>
              </div>

              <div style={{
                background: '#fff',
                border: '1px solid #E5E5E5',
                borderRadius: '8px',
                padding: '1.5rem',
                transition: 'all 0.3s ease'
              }}>
                <div style={{
                  fontSize: '0.85rem',
                  color: '#666',
                  marginBottom: '0.5rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 500
                }}>
                  Pajamos
                </div>
                <div style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '2.5rem',
                  fontWeight: 600,
                  color: '#0066ff',
                  marginBottom: '0.25rem'
                }}>
                  €{stats.totalRevenue.toLocaleString()}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#999' }}>šį mėnesį</div>
              </div>
            </div>

            <div className="card" style={{ padding: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: '#000042',
                  margin: 0
                }}>🕒 Paskutinė veikla</h3>
                <span className="badge badge-primary">{recentActivity.length} įrašai</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                {recentActivity.length > 0 ? recentActivity.map((activity, index) => (
                  <div key={index} style={{
                    padding: 'var(--spacing-md)',
                    background: 'var(--neutral-light)',
                    borderRadius: '6px',
                    border: '1px solid var(--neutral-border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-md)'
                  }}>
                    <div style={{ fontSize: '1.5rem' }}></div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: 'var(--primary-navy)', marginBottom: '0.25rem' }}>
                        {activity.user} {activity.action}
                      </div>
                      <div style={{ color: 'var(--secondary-gray)', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                        {activity.details}
                      </div>
                      <div style={{ color: 'var(--secondary-gray)', fontSize: '0.8rem' }}>
                        {activity.time}
                      </div>
                    </div>
                  </div>
                )) : (
                  <div style={{
                    textAlign: 'center',
                    padding: 'var(--spacing-xl)',
                    color: 'var(--secondary-gray)'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-md)' }}>📭</div>
                    <p className="lithuanian-text">Šiuo metu nėra jokios veiklos</p>
                  </div>
                )}
              </div>
            </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div style={{
              background: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              padding: '2rem',
              margin: '1rem 0'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div>
                  <h3 style={{
                    color: '#000042',
                    margin: '0 0 0.5rem 0',
                    fontSize: '1.5rem',
                    fontWeight: '600'
                  }}>👥 Vartotojų sąrašas</h3>
                  <p style={{
                    color: '#666',
                    margin: 0,
                    fontSize: '0.95rem'
                  }}>Valdykite ir peržiūrėkite visus registruotus vartotojus</p>
                </div>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder="Ieškoti vartotojo..."
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      minWidth: '200px',
                      outline: 'none'
                    }}
                  />
                  <button
                    style={{
                      background: '#000042',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}
                    onClick={() => setShowAddUserModal(true)}
                  >
                    Pridėti vartotoją
                  </button>
                </div>
              </div>

              <div style={{
                overflowX: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                background: '#f8f9fa'
              }}>
                <table style={{
                  width: '100%',
                  minWidth: '1000px',
                  borderCollapse: 'collapse',
                  fontSize: '0.9rem'
                }}>
                  <thead>
                    <tr style={{
                      background: '#000042',
                      color: 'white'
                    }}>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        borderBottom: '2px solid #e0e0e0',
                        whiteSpace: 'nowrap'
                      }}>Vardas</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        borderBottom: '2px solid #e0e0e0',
                        whiteSpace: 'nowrap'
                      }}>Profesija</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        borderBottom: '2px solid #e0e0e0',
                        whiteSpace: 'nowrap'
                      }}>Regionas</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        borderBottom: '2px solid #e0e0e0',
                        whiteSpace: 'nowrap'
                      }}>El. pašto adresas</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        borderBottom: '2px solid #e0e0e0',
                        whiteSpace: 'nowrap'
                      }}>Telefonas</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        borderBottom: '2px solid #e0e0e0',
                        whiteSpace: 'nowrap'
                      }}>Statusas</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'center',
                        fontWeight: '600',
                        borderBottom: '2px solid #e0e0e0',
                        whiteSpace: 'nowrap'
                      }}>AI Agentas</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        borderBottom: '2px solid #e0e0e0',
                        whiteSpace: 'nowrap'
                      }}>Registracijos data</th>
                      <th style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'left',
                        fontWeight: '600',
                        borderBottom: '2px solid #e0e0e0',
                        whiteSpace: 'nowrap'
                      }}>Veiksmai</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={user.id} style={{
                        background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                        borderBottom: '1px solid #e0e0e0'
                      }}>
                        <td style={{
                          padding: '0.75rem',
                          fontWeight: '600',
                          color: '#000042',
                          whiteSpace: 'nowrap'
                        }}>{user.vardas}</td>
                        <td style={{
                          padding: '0.75rem',
                          color: '#333',
                          whiteSpace: 'nowrap'
                        }}>{user.profession_label || user.profesijos_tipas}</td>
                        <td style={{
                          padding: '0.75rem',
                          color: '#333',
                          whiteSpace: 'nowrap'
                        }}>{user.darbo_regionas}</td>
                        <td style={{
                          padding: '0.75rem',
                          color: '#333',
                          whiteSpace: 'nowrap'
                        }}>{user.el_pastas}</td>
                        <td style={{
                          padding: '0.75rem',
                          color: '#333',
                          whiteSpace: 'nowrap'
                        }}>{user.telefonas}</td>
                        <td style={{
                          padding: '0.75rem',
                          whiteSpace: 'nowrap'
                        }}>
                          <span style={{
                            background: user.statusas?.toLowerCase() === 'aktyvus' ? '#28a745' : '#ffc107',
                            color: user.statusas?.toLowerCase() === 'aktyvus' ? 'white' : '#212529',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.8rem',
                            fontWeight: '500'
                          }}>
                            {user.statusas || 'Laukiama'}
                          </span>
                        </td>
                        <td style={{
                          padding: '0.75rem',
                          textAlign: 'center'
                        }}>
                          <button
                            onClick={async () => {
                              try {
                                const response = await fetch(`/api/admin/users/${user.id}/toggle-ai`, {
                                  method: 'POST',
                                  credentials: 'include',
                                  headers: {
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify({
                                    ai_agent_ijungtas: !user.ai_agent_ijungtas
                                  })
                                });

                                if (response.ok) {
                                  // Update local state
                                  setUsers(users.map(u =>
                                    u.id === user.id
                                      ? { ...u, ai_agent_ijungtas: !u.ai_agent_ijungtas }
                                      : u
                                  ));
                                }
                              } catch (error) {
                                console.error('Toggle AI error:', error);
                              }
                            }}
                            style={{
                              background: user.ai_agent_ijungtas ? '#28a745' : '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '20px',
                              padding: '0.3rem 0.8rem',
                              fontSize: '0.8rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease'
                            }}
                            title={user.ai_agent_ijungtas ? 'AI agentas įjungtas' : 'AI agentas išjungtas'}
                          >
                            {user.ai_agent_ijungtas ? 'ON' : 'OFF'}
                          </button>
                        </td>
                        <td style={{
                          padding: '0.75rem',
                          color: '#333',
                          whiteSpace: 'nowrap'
                        }}>
                          {new Date(user.sukurta_data).toLocaleDateString('lt-LT')}
                        </td>
                        <td style={{
                          padding: '0.75rem',
                          whiteSpace: 'nowrap'
                        }}>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button
                              style={{
                                background: '#000042',
                                color: 'white',
                                border: 'none',
                                padding: '0.4rem 0.8rem',
                                fontSize: '0.8rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                              onClick={() => navigate(`/admin/user/${user.id}`)}
                            >
                              Peržiūrėti
                            </button>
                            <button
                              style={{
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                padding: '0.4rem 0.8rem',
                                fontSize: '0.8rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                              onClick={async () => {
                                if (window.confirm(`Ar tikrai norite ištrinti vartotoją ${user.vardas}? Šis veiksmas neatšaukiamas.`)) {
                                  try {
                                    const response = await fetch(`/api/admin/users/${user.id}`, {
                                      method: 'DELETE',
                                      credentials: 'include'
                                    });

                                    const result = await response.json();

                                    if (result.success) {
                                      alert(result.message);
                                      // Refresh the users list
                                      fetchUsers();
                                    } else {
                                      alert('Klaida: ' + result.message);
                                    }
                                  } catch (error) {
                                    console.error('Delete user error:', error);
                                    alert('Klaida trinant vartotoją');
                                  }
                                }
                              }}
                            >
                              Ištrinti
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--spacing-xl)' }}>
              <div className="dashboard-card">
                <div className="section-header">
                  <h3 className="section-title lithuanian-heading">Mėnesinė naudojimo statistika</h3>
                  <p className="section-subtitle lithuanian-text">Vartotojų registracijos per paskutinius 6 mėnesius</p>
                </div>

                {analyticsLoading ? (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '300px',
                    color: '#666'
                  }}>
                    ⏳ Kraunami analitikos duomenys...
                  </div>
                ) : analyticsData ? (
                  <div style={{
                    background: 'var(--neutral-light)',
                    padding: 'var(--spacing-lg)',
                    borderRadius: '8px',
                    border: '1px solid var(--neutral-border)',
                    minHeight: '300px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'end',
                      justifyContent: 'space-around',
                      height: '250px',
                      gap: 'var(--spacing-md)'
                    }}>
                      {analyticsData?.monthlyChart?.map((data: any, index: number) => (
                        <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                          <div style={{
                            background: 'linear-gradient(45deg, var(--primary-navy), var(--primary-navy-light))',
                            width: '100%',
                            height: data.height,
                            borderRadius: '4px 4px 0 0',
                            position: 'relative',
                            transition: 'all 0.3s ease'
                          }}>
                            <div style={{
                              position: 'absolute',
                              top: '-30px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              background: 'var(--primary-navy)',
                              color: 'white',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.8rem',
                              fontWeight: '600'
                            }}>
                              {data.value}
                            </div>
                          </div>
                          <div style={{
                            marginTop: 'var(--spacing-sm)',
                            color: 'var(--secondary-gray)',
                            fontWeight: '600',
                            fontSize: '0.9rem'
                          }}>
                            {data.month}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{
                    background: 'var(--neutral-light)',
                    padding: 'var(--spacing-lg)',
                    borderRadius: '8px',
                    border: '1px solid var(--neutral-border)',
                    minHeight: '300px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#666'
                  }}>
                    Nepavyko gauti analitikos duomenų
                  </div>
                )}
              </div>

              <div className="dashboard-card">
                <div className="section-header">
                  <h3 className="section-title lithuanian-heading">Pagrindiniai rodikliai</h3>
                  <p className="section-subtitle lithuanian-text">Realūs sistemos duomenys</p>
                </div>

                {analyticsLoading ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    ⏳ Kraunama...
                  </div>
                ) : analyticsData ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                    {analyticsData.metrics.map((metric: any, index: number) => (
                      <div key={index} style={{
                        padding: 'var(--spacing-md)',
                        background: 'var(--neutral-light)',
                        borderRadius: '6px',
                        border: '1px solid var(--neutral-border)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <div style={{ color: 'var(--secondary-gray)', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                            {metric.label}
                          </div>
                          <div style={{ fontWeight: '700', color: 'var(--primary-navy)', fontSize: '1.1rem' }}>
                            {metric.value}
                          </div>
                        </div>
                        <div style={{
                          background: metric.trend.startsWith('+') ? 'var(--accent-success)' : 'var(--secondary-gray)',
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: '600'
                        }}>
                          {metric.trend}
                        </div>
                      </div>
                    ))}

                    {!analyticsData.sessionData.hasData && (
                      <div style={{
                        padding: 'var(--spacing-md)',
                        background: '#fff3cd',
                        borderRadius: '6px',
                        border: '1px solid #ffeaa7',
                        color: '#856404',
                        fontSize: '0.9rem',
                        textAlign: 'center'
                      }}>
                        Kai vartotojai pradės naudoti AI asistentą, čia matysite realius pokalbių duomenis
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#dc3545' }}>
                    Klaida gaunant duomenis
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'payments' && (
            <AdminPaymentManagement />
          )}

          {activeTab === 'recordings' && (
            <div className="dashboard-card">
              <div className="section-header">
                <h3 className="section-title lithuanian-heading">Skambučių įrašai ir istorija</h3>
                <p className="section-subtitle lithuanian-text">Peržiūrėkite, klausykitės ir valdykite visus garso įrašus</p>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--spacing-lg)',
                padding: 'var(--spacing-md)',
                background: 'var(--neutral-light)',
                borderRadius: '6px',
                border: '1px solid var(--neutral-border)'
              }}>
                <div style={{ color: 'var(--secondary-gray)' }}>
                  Viso įrašų: <strong style={{ color: 'var(--primary-navy)' }}>{recordings.length}</strong>
                </div>
                <button
                  onClick={() => {
                    if (!recordingsLoading) {
                      fetchRecordings();
                    }
                  }}
                  disabled={recordingsLoading}
                  className={recordingsLoading ? 'btn-secondary' : 'btn-primary'}
                  style={{
                    opacity: recordingsLoading ? 0.6 : 1,
                    cursor: recordingsLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {recordingsLoading ? 'Kraunama...' : 'Atnaujinti'}
                </button>
              </div>

              {/* Filters */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 'var(--spacing-md)',
                marginBottom: 'var(--spacing-lg)',
                padding: 'var(--spacing-md)',
                background: 'var(--neutral-light)',
                borderRadius: '6px',
                border: '1px solid var(--neutral-border)'
              }}>
                <div className="form-group">
                  <label className="form-label">
                    Filtruoti pagal vartotoją:
                  </label>
                  <select
                    value={recordingsFilter.userId}
                    onChange={(e) => setRecordingsFilter({...recordingsFilter, userId: e.target.value})}
                    className="form-control"
                  >
                    <option value="">-- Visi vartotojai --</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.vardas} ({user.el_pastas})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Nuo datos:
                  </label>
                  <input
                    type="date"
                    value={recordingsFilter.dateFrom}
                    onChange={(e) => setRecordingsFilter({...recordingsFilter, dateFrom: e.target.value})}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Iki datos:
                  </label>
                  <input
                    type="date"
                    value={recordingsFilter.dateTo}
                    onChange={(e) => setRecordingsFilter({...recordingsFilter, dateTo: e.target.value})}
                    className="form-control"
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'end' }}>
                  <button
                    onClick={() => fetchRecordings()}
                    disabled={recordingsLoading}
                    className="btn-cta"
                    style={{
                      cursor: recordingsLoading ? 'not-allowed' : 'pointer',
                      opacity: recordingsLoading ? 0.6 : 1
                    }}
                  >
                    🔍 Filtruoti
                  </button>
                </div>
              </div>

              {/* Results */}
              {recordingsLoading ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  background: '#ffffff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  color: '#757575'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
                  Kraunami skambučių įrašai...
                </div>
              ) : recordings.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  background: '#ffffff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  color: '#757575'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}></div>
                  <h4 style={{ color: '#424242', marginBottom: '1rem' }}>Skambučių įrašų nerasta</h4>
                  <p>Kai vartotojai atliks skambučius su AI asistentu, čia matysite visus įrašus.</p>
                </div>
              ) : (
                <div>
                  <div style={{
                    background: '#ffffff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    marginBottom: '1rem',
                    padding: '1rem'
                  }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#424242' }}>
                      Statistika: {recordings.length} įrašai rasti
                    </h4>
                    <div style={{ color: '#757575', fontSize: '0.9rem' }}>
                      Bendras dydis: {(recordings.reduce((total, r) => total + (r.size_bytes || 0), 0) / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {recordings.map((recording) => (
                      <div
                        key={recording.id}
                        style={{
                          background: '#ffffff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          padding: '1.5rem',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          transition: 'transform 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 3px 6px rgba(0,0,0,0.15)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '1rem'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              marginBottom: '0.5rem'
                            }}>
                              <h4 style={{ margin: 0, color: '#424242' }}>
                                Skambutis #{recording.session_id ? recording.session_id.slice(-8) : recording.id.slice(-8)}
                              </h4>
                              {recording.format && (
                                <span style={{
                                  background: '#007bff',
                                  color: 'white',
                                  padding: '0.2rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold'
                                }}>
                                  {recording.format.toUpperCase()}
                                </span>
                              )}
                              {!recording.format && recording.ai_summary && (
                                <span style={{
                                  background: '#28a745',
                                  color: 'white',
                                  padding: '0.2rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold'
                                }}>
                                  APDOROTA
                                </span>
                              )}
                            </div>
                            <div style={{ color: '#757575', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                              {recording.user_name && (
                                <span style={{ marginRight: '1rem' }}>
                                  {recording.user_name}
                                </span>
                              )}
                              {recording.user_profession && (
                                <span style={{ marginRight: '1rem' }}>
                                  {recording.user_profession}
                                </span>
                              )}
                              {recording.user_email && (
                                <span style={{ marginRight: '1rem' }}>
                                  {recording.user_email}
                                </span>
                              )}
                            </div>
                            <div style={{ color: '#757575', fontSize: '0.9rem' }}>
                              {new Date(recording.recorded_at).toLocaleString('lt-LT')}
                              {recording.duration_seconds && (
                                <span style={{ marginLeft: '1rem' }}>
                                  {Math.floor(recording.duration_seconds / 60)}:{(recording.duration_seconds % 60).toString().padStart(2, '0')}
                                </span>
                              )}
                              {recording.size_bytes && (
                                <span style={{ marginLeft: '1rem' }}>
                                  {(recording.size_bytes / 1024 / 1024).toFixed(2)} MB
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              // Use recording_id if available, otherwise use session id
                              const detailId = recording.recording_id || recording.id;
                              navigate(`/admin/recording/${detailId}`);
                            }}
                            style={{
                              background: recording.recording_id ? '#007bff' : '#17a2b8',
                              color: 'white',
                              border: 'none',
                              padding: '0.5rem 1rem',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              transition: 'background 0.3s ease',
                              minWidth: '120px'
                            }}
                          >
                            {recording.recording_id ? 'Peržiūrėti įrašą' : 'Peržiūrėti detales'}
                          </button>
                        </div>

                        {/* AI Summary Preview */}
                        {recording.ai_summary && (
                          <div style={{
                            background: '#fff3cd',
                            padding: '1rem',
                            borderRadius: '4px',
                            border: '1px solid #ffc107',
                            marginTop: '1rem'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              marginBottom: '0.5rem'
                            }}>
                              <h5 style={{ margin: 0, color: '#856404' }}>AI Santrauka</h5>
                              {recording.ai_summary.has_important_info && (
                                <span style={{
                                  background: '#dc3545',
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  padding: '0.2rem 0.4rem',
                                  borderRadius: '4px',
                                  fontWeight: 'bold'
                                }}>
                                  SVARBU
                                </span>
                              )}
                            </div>
                            {recording.ai_summary.trumpa_santrauka && (
                              <p style={{
                                margin: 0,
                                color: '#856404',
                                fontSize: '0.9rem',
                                lineHeight: '1.5'
                              }}>
                                {recording.ai_summary.trumpa_santrauka}
                              </p>
                            )}
                            {recording.ai_summary.kliento_poreikis && (
                              <p style={{
                                margin: '0.5rem 0 0 0',
                                color: '#856404',
                                fontSize: '0.85rem'
                              }}>
                                <strong>Poreikis:</strong> {recording.ai_summary.kliento_poreikis}
                              </p>
                            )}
                            {recording.ai_summary.sekantis_zingsnis && (
                              <p style={{
                                margin: '0.5rem 0 0 0',
                                color: '#004085',
                                fontSize: '0.85rem',
                                background: '#cce5ff',
                                padding: '0.5rem',
                                borderRadius: '4px',
                                marginTop: '0.5rem'
                              }}>
                                <strong>Sekantis žingsnis:</strong> {recording.ai_summary.sekantis_zingsnis}
                              </p>
                            )}
                          </div>
                        )}

                        {recording.transcript && (
                          <div style={{
                            background: '#f8f9fa',
                            padding: '1rem',
                            borderRadius: '4px',
                            border: '1px solid #e9ecef',
                            marginTop: '1rem'
                          }}>
                            <h5 style={{ margin: '0 0 0.5rem 0', color: '#424242' }}>Transkriptas:</h5>
                            <p style={{
                              margin: 0,
                              color: '#6c757d',
                              fontSize: '0.9rem',
                              lineHeight: '1.5',
                              fontStyle: 'italic',
                              maxHeight: '100px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {recording.transcript}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'test-voice' && (
            <div className="dashboard-card">
              <div className="section-header">
                <h3 className="section-title lithuanian-heading">Balso asistentas testas</h3>
                <p className="section-subtitle lithuanian-text">
                  Pasirinkite vartotoją ir profesiją, kad išbandytumėte balso asistentą su personalizuotais prompt'ais
                </p>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: 'var(--spacing-lg)',
                marginBottom: 'var(--spacing-xl)'
              }}>
                <div className="form-group">
                  <label htmlFor="user-select" className="form-label">Pasirinkite vartotoją:</label>
                  <select
                    id="user-select"
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="form-control"
                  >
                    <option value="">-- Pasirinkite vartotoją --</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.vardas} - {user.profesijos_tipas} ({user.darbo_regionas})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="profession-select" className="form-label">Arba pasirinkite profesiją:</label>
                  <select
                    id="profession-select"
                    value={selectedProfession}
                    onChange={(e) => setSelectedProfession(e.target.value)}
                    className="form-control"
                  >
                    <option value="">-- Pasirinkite profesiją --</option>
                    <option value="dazytojas">Dažytojas</option>
                    <option value="stalius">Stalius</option>
                    <option value="santechnikas">🚰 Santechnikas</option>
                    <option value="elektrikas">Elektrikas</option>
                    <option value="stogdengys">Stogdengys</option>
                  </select>
                </div>
              </div>

              <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
                <button
                  className="btn-cta"
                  onClick={async () => {
                    if (!selectedUser && !selectedProfession) {
                      alert('Pasirinkite vartotoją arba profesiją');
                      return;
                    }

                    setTestCallStatus('Generuojamas prompt...');

                    try {
                      let promptResponse;
                      if (selectedUser) {
                        // Get user-specific prompt
                        promptResponse = await fetch(`/api/prompt/${selectedUser}`, {
                          credentials: 'include'
                        });
                      } else {
                        // Get profession template
                        promptResponse = await fetch(`/api/template/${selectedProfession}`, {
                          credentials: 'include'
                        });
                      }

                      if (promptResponse.ok) {
                        const promptData = await promptResponse.json();
                        setTestCallStatus('Prompt sugeneruotas sėkmingai!');

                        // Display the generated prompt with debug info
                        const promptWindow = window.open('', '_blank', 'width=1200,height=800');
                        if (promptWindow && selectedUser && promptData) {
                          // For user prompts, show services and full personalized prompt
                          const services = promptData.userData?.custom_paslaugos
                            ? (typeof promptData.userData.custom_paslaugos === 'string'
                              ? JSON.parse(promptData.userData.custom_paslaugos)
                              : promptData.userData.custom_paslaugos)
                            : [];

                          promptWindow.document.write(`
                            <html>
                              <head>
                                <title>Sugeneruotas AI Prompt - ${promptData.userData?.vardas || 'Vartotojas'}</title>
                                <style>
                                  body { font-family: Arial, sans-serif; padding: 20px; background: #f9f9f9; }
                                  h2 { color: #000042; }
                                  .section { background: white; padding: 15px; margin: 15px 0; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
                                  .services { background: #e8f4f8; padding: 10px; border-radius: 5px; margin: 10px 0; }
                                  pre { background: #f5f5f5; padding: 15px; border-radius: 5px; white-space: pre-wrap; overflow-x: auto; }
                                  .label { font-weight: bold; color: #555; }
                                  .warning { background: #fff3cd; color: #856404; padding: 10px; border-radius: 5px; margin: 10px 0; }
                                </style>
                              </head>
                              <body>
                                <h2>🎤 Sugeneruotas AI Prompt - Balso testas</h2>

                                <div class="section">
                                  <h3>Vartotojo duomenys:</h3>
                                  <p><span class="label">Vardas:</span> ${promptData.userData?.vardas || 'Nenurodyta'}</p>
                                  <p><span class="label">Profesija:</span> ${promptData.userData?.profesijos_tipas || 'Nenurodyta'}</p>
                                  <p><span class="label">AI numeris:</span> ${promptData.userData?.ai_numeris || 'Nenurodyta'}</p>
                                  <p><span class="label">Miestas:</span> ${promptData.userData?.darbo_miestas || 'Nenurodyta'}</p>
                                </div>

                                ${services.length > 0 ? `
                                  <div class="section">
                                    <h3>Teikiamos paslaugos (${services.length}):</h3>
                                    <div class="services">
                                      ${services.map((s) => `
                                        <p>• <strong>${s.paslauga}</strong> - ${s.kaina > 0 ? `€${s.kaina}/${s.vienetas}` : 'Kaina derinama'}</p>
                                      `).join('')}
                                    </div>
                                  </div>
                                ` : `
                                  <div class="section warning">
                                    <p><strong>⚠️ Paslaugos neįvestos!</strong></p>
                                    <p>Vartotojas neužpildė paslaugų sąrašo registracijos metu. AI nežinos konkrečių paslaugų ir kainų.</p>
                                  </div>
                                `}

                                <div class="section">
                                  <h3>Pilnas AI promptas (su visais duomenimis):</h3>
                                  <pre>${promptData.prompt || 'Promptas nerastas'}</pre>
                                </div>

                                ${promptData.replacements ? `
                                  <div class="section">
                                    <h3>Įterpti duomenys (debug):</h3>
                                    <pre>${JSON.stringify(promptData.replacements, null, 2)}</pre>
                                  </div>
                                ` : ''}
                              </body>
                            </html>
                          `);
                          promptWindow.document.close();
                        } else if (promptWindow) {
                          // For template prompts (no user selected)
                          promptWindow.document.write(`
                            <html>
                              <head><title>Promptų šablonas</title></head>
                              <body style="font-family: Arial, sans-serif; padding: 20px;">
                                <h2>AI Promptų šablonas - ${selectedProfession || 'Profesija'}</h2>
                                <p style="background: #fff3cd; padding: 10px; border-radius: 5px;">
                                  ⚠️ Tai yra šablonas. Pasirinkite vartotoją, kad matytumėte personalizuotą promptą su paslaugomis.
                                </p>
                                <pre style="background: #f5f5f5; padding: 15px; border-radius: 5px; white-space: pre-wrap;">
                                  ${promptData?.template?.template || promptData?.prompt || 'Šablonas nerastas'}
                                </pre>
                              </body>
                            </html>
                          `);
                          promptWindow.document.close();
                        }
                      } else {
                        setTestCallStatus('Klaida generuojant prompt');
                      }
                    } catch (error) {
                      console.error('Error generating prompt:', error);
                      setTestCallStatus('Klaida: ' + (error as Error).message);
                    }
                  }}
                  disabled={!selectedUser && !selectedProfession}
                  style={{
                    opacity: (!selectedUser && !selectedProfession) ? 0.6 : 1,
                    cursor: (!selectedUser && !selectedProfession) ? 'not-allowed' : 'pointer'
                  }}
                >
                  Generuoti Prompt
                </button>
              </div>

              <div className="card" style={{ padding: 'var(--spacing-lg)' }}>
                <h4 className="lithuanian-heading" style={{ marginBottom: 'var(--spacing-md)' }}>Informacija:</h4>
                <ul style={{ lineHeight: '1.7', color: 'var(--secondary-gray)' }}>
                  <li><strong style={{ color: 'var(--primary-navy)' }}>Prompt generavimas:</strong> Rodo, kaip AI prompt atrodo su konkrečiais vartotojo duomenimis</li>
                  <li><strong style={{ color: 'var(--primary-navy)' }}>WebRTC balso testas:</strong> Realaus laiko balso sąveika su AI naudojant personalius promptus</li>
                  <li><strong style={{ color: 'var(--primary-navy)' }}>Profesijos šablonai:</strong> Rodo bendrus prompt'us kiekvienai profesijai</li>
                  <li><strong style={{ color: 'var(--primary-navy)' }}>Funkcionalumas:</strong> Testuoja AI įrankius (kainos apskaičiavimas, kontaktų rinkimas, etc.)</li>
                </ul>
              </div>

              {/* WebRTC Real-time Voice Test */}
              <WebRTCTest
                selectedUser={selectedUser}
                selectedProfession={selectedProfession}
              />
            </div>
          )}

          {activeTab === 'requests' && (
            <div>
              <h2 className="section-title" style={{ marginBottom: '2rem' }}>📨 Vartotojų prašymai</h2>

              {/* Filters */}
              <div style={{
                background: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '1.5rem',
                marginBottom: '2rem'
              }}>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <select
                    value={requestsFilter.status}
                    onChange={(e) => setRequestsFilter({ ...requestsFilter, status: e.target.value })}
                    style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="">Visi statusai</option>
                    <option value="naujas">Naujas</option>
                    <option value="peržiūrimas">Peržiūrimas</option>
                    <option value="atsakytas">Atsakytas</option>
                    <option value="uždarytas">Uždarytas</option>
                    <option value="atmestas">Atmestas</option>
                  </select>

                  <select
                    value={requestsFilter.priority}
                    onChange={(e) => setRequestsFilter({ ...requestsFilter, priority: e.target.value })}
                    style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="">Visi prioritetai</option>
                    <option value="žemas">Žemas</option>
                    <option value="vidutinis">Vidutinis</option>
                    <option value="aukštas">Aukštas</option>
                    <option value="skubus">Skubus</option>
                  </select>

                  <button
                    className="btn-primary"
                    onClick={fetchRequests}
                    style={{ padding: '0.5rem 1.5rem' }}
                  >
                    🔍 Filtruoti
                  </button>
                </div>
              </div>

              {/* Requests Table */}
              {requestsLoading ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <div style={{ fontSize: '2rem' }}>⏳</div>
                  <p>Kraunami prašymai...</p>
                </div>
              ) : requests.length === 0 ? (
                <div style={{
                  background: '#ffffff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  padding: '3rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                  <p>Prašymų nėra</p>
                </div>
              ) : (
                <div style={{
                  background: '#ffffff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa' }}>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>ID</th>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Vartotojas</th>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Tema</th>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Statusas</th>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Prioritetas</th>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Data</th>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Veiksmai</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((request) => (
                        <tr key={request.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '1rem', fontSize: '0.8rem', color: '#666' }}>
                            {request.id.substring(0, 8)}...
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <div>
                              <strong>{request.vartotojo_vardas || 'Nežinomas'}</strong>
                              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                {request.vartotojo_el_pastas}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <div>{request.prasymo_tema}</div>
                            {request.pokalbio_santrauka && (
                              <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                                {request.pokalbio_santrauka.substring(0, 80)}...
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              background:
                                request.prasymo_statusas === 'naujas' ? '#fef3c7' :
                                request.prasymo_statusas === 'peržiūrimas' ? '#dbeafe' :
                                request.prasymo_statusas === 'atsakytas' ? '#d1fae5' :
                                request.prasymo_statusas === 'uždarytas' ? '#e5e7eb' :
                                '#fee2e2',
                              color:
                                request.prasymo_statusas === 'naujas' ? '#92400e' :
                                request.prasymo_statusas === 'peržiūrimas' ? '#1e40af' :
                                request.prasymo_statusas === 'atsakytas' ? '#065f46' :
                                request.prasymo_statusas === 'uždarytas' ? '#374151' :
                                '#991b1b'
                            }}>
                              {request.prasymo_statusas}
                            </span>
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              background:
                                request.prioritetas === 'skubus' ? '#fee2e2' :
                                request.prioritetas === 'aukštas' ? '#fed7aa' :
                                request.prioritetas === 'vidutinis' ? '#dbeafe' :
                                '#e5e7eb',
                              color:
                                request.prioritetas === 'skubus' ? '#991b1b' :
                                request.prioritetas === 'aukštas' ? '#9a3412' :
                                request.prioritetas === 'vidutinis' ? '#1e40af' :
                                '#374151'
                            }}>
                              {request.prioritetas}
                            </span>
                          </td>
                          <td style={{ padding: '1rem', fontSize: '0.85rem' }}>
                            {new Date(request.sukurta_data).toLocaleDateString('lt-LT', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <button
                              className="btn-primary"
                              onClick={() => {
                                setSelectedRequest(request);
                                setShowRequestModal(true);
                              }}
                              style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                            >
                              👁️ Peržiūrėti
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'help-chats' && (
            <div>
              <h2 className="section-title" style={{ marginBottom: '2rem' }}>💬 Pagalbos pokalbiai</h2>
              <p style={{ marginBottom: '2rem', color: '#666' }}>
                Visi vartotojų pokalbiai su AI pagalbos asistentu (tekstiniai ir balsiniai)
              </p>

              {conversationsLoading ? (
                <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
                  <p>Kraunama...</p>
                </div>
              ) : helpChatConversations.length === 0 ? (
                <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
                  <p>Pokalbių kol kas nėra</p>
                </div>
              ) : (
                <div className="card-grid">
                  {helpChatConversations.map((conv, index) => (
                    <div key={index} className="card">
                      <h3 style={{ marginBottom: '1rem', color: '#0066ff' }}>
                        {conv.is_voice_call ? '🎤' : '💬'} {conv.user_name || 'Anonimas'}
                      </h3>
                      <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#666' }}>
                        <div><strong>El. paštas:</strong> {conv.user_email || 'N/A'}</div>
                        <div><strong>Telefonas:</strong> {conv.user_phone || 'N/A'}</div>
                        <div><strong>Profesija:</strong> {conv.profession || 'N/A'}</div>
                        <div><strong>Tipas:</strong> {conv.is_voice_call ? 'Balso skambutis' : 'Tekstinis'}</div>
                        <div><strong>Žinučių:</strong> {conv.message_count}</div>
                        <div><strong>Pradžia:</strong> {new Date(conv.session_start).toLocaleString('lt-LT')}</div>
                      </div>
                      <details>
                        <summary style={{ cursor: 'pointer', color: '#0066ff', marginBottom: '0.5rem' }}>
                          Rodyti pokalbį
                        </summary>
                        <div style={{ maxHeight: '300px', overflowY: 'auto', padding: '1rem', background: '#f5f5f5', borderRadius: '8px' }}>
                          {conv.messages.map((msg: any, msgIndex: number) => (
                            <div key={msgIndex} style={{
                              marginBottom: '1rem',
                              padding: '0.5rem',
                              background: msg.role === 'user' ? '#e3f2fd' : '#fff',
                              borderRadius: '4px',
                              borderLeft: `3px solid ${msg.role === 'user' ? '#2196f3' : '#4caf50'}`
                            }}>
                              <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>
                                <strong>{msg.role === 'user' ? '👤 Vartotojas' : '🤖 Asistentas'}</strong> - {new Date(msg.timestamp).toLocaleTimeString('lt-LT')}
                              </div>
                              <div>{msg.content}</div>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <AdminSettings />
          )}

          {activeTab === 'logs' && (
            <AdminLogs />
          )}
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginBottom: '1.5rem', color: '#000042' }}>Pridėti naują vartotoją</h2>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Vardas *
              </label>
              <input
                type="text"
                value={newUserData.vardas}
                onChange={(e) => setNewUserData({...newUserData, vardas: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
                placeholder="Pvz: Jonas Jonaitis"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                El. paštas *
              </label>
              <input
                type="email"
                value={newUserData.el_pastas}
                onChange={(e) => setNewUserData({...newUserData, el_pastas: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
                placeholder="vardas@example.com"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Telefonas
              </label>
              <input
                type="tel"
                value={newUserData.telefonas}
                onChange={(e) => setNewUserData({...newUserData, telefonas: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
                placeholder="+370 600 00000"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Profesija *
              </label>
              <select
                value={newUserData.profesijos_tipas}
                onChange={(e) => setNewUserData({...newUserData, profesijos_tipas: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              >
                <option value="">-- Pasirinkite profesiją --</option>
                <option value="dazytojas">Dažytojas</option>
                <option value="stalius">Stalius</option>
                <option value="santechnikas">Santechnikas</option>
                <option value="elektrikas">Elektrikas</option>
                <option value="stogdengys">Stogdengys</option>
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Darbo regionas
              </label>
              <input
                type="text"
                value={newUserData.darbo_regionas}
                onChange={(e) => setNewUserData({...newUserData, darbo_regionas: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
                placeholder="Pvz: Vilnius"
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddUserModal(false);
                  setNewUserData({
                    vardas: '',
                    el_pastas: '',
                    telefonas: '',
                    profesijos_tipas: '',
                    darbo_regionas: ''
                  });
                }}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Atšaukti
              </button>
              <button
                onClick={async () => {
                  if (!newUserData.vardas || !newUserData.el_pastas || !newUserData.profesijos_tipas) {
                    alert('Užpildykite visus privalomus laukus (*)');
                    return;
                  }

                  try {
                    const response = await fetch('/api/admin/users', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      credentials: 'include',
                      body: JSON.stringify(newUserData)
                    });

                    const result = await response.json();

                    if (result.success) {
                      alert(result.message);
                      setShowAddUserModal(false);
                      setNewUserData({
                        vardas: '',
                        el_pastas: '',
                        telefonas: '',
                        profesijos_tipas: '',
                        darbo_regionas: ''
                      });
                      fetchUsers();
                    } else {
                      alert('Klaida: ' + result.message);
                    }
                  } catch (error) {
                    console.error('Add user error:', error);
                    alert('Klaida kuriant vartotoją');
                  }
                }}
                style={{
                  background: '#000042',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}
              >
                Sukurti vartotoją
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Detail Modal */}
      {showRequestModal && selectedRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          overflow: 'auto'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            maxWidth: '900px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0 }}>📨 Prašymo detalės</h3>
              <button
                onClick={() => setShowRequestModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '1.5rem' }}>
              {/* User Info */}
              <div style={{
                background: '#f8f9fa',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{ margin: '0 0 0.5rem 0' }}>👤 Vartotojas</h4>
                <div><strong>Vardas:</strong> {selectedRequest.vartotojo_vardas || 'Nežinomas'}</div>
                <div><strong>El. paštas:</strong> {selectedRequest.vartotojo_el_pastas}</div>
                <div><strong>Telefonas:</strong> {selectedRequest.vartotojo_telefonas || 'Nenurodytas'}</div>
                <div><strong>Profesija:</strong> {selectedRequest.vartotojo_profesija || 'Nenurodyta'}</div>
              </div>

              {/* Request Info */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ margin: '0 0 0.5rem 0' }}>📝 Prašymas</h4>
                <div><strong>Tema:</strong> {selectedRequest.prasymo_tema}</div>
                <div style={{ marginTop: '0.5rem' }}><strong>Tekstas:</strong></div>
                <div style={{
                  background: '#f8f9fa',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginTop: '0.5rem',
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedRequest.prasymo_tekstas}
                </div>
              </div>

              {/* AI Summary */}
              {selectedRequest.pokalbio_santrauka && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0' }}>🤖 AI santrauka</h4>
                  <div style={{
                    background: '#e0f2fe',
                    padding: '1rem',
                    borderRadius: '8px',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {selectedRequest.pokalbio_santrauka}
                  </div>
                </div>
              )}

              {/* Conversation History */}
              {selectedRequest.pokalbio_istorija && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0' }}>💬 Pokalbio istorija</h4>
                  <div style={{
                    background: '#f8f9fa',
                    padding: '1rem',
                    borderRadius: '8px',
                    maxHeight: '300px',
                    overflow: 'auto'
                  }}>
                    {(typeof selectedRequest.pokalbio_istorija === 'string'
                      ? JSON.parse(selectedRequest.pokalbio_istorija)
                      : selectedRequest.pokalbio_istorija
                    ).map((msg: any, idx: number) => (
                      <div key={idx} style={{
                        marginBottom: '0.75rem',
                        padding: '0.5rem',
                        background: msg.role === 'user' ? '#dbeafe' : '#f0fdf4',
                        borderRadius: '6px'
                      }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                          {msg.role === 'user' ? '👤 Vartotojas' : '🤖 AI'}
                        </div>
                        <div style={{
                          fontSize: '0.9rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {msg.content}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status Update */}
              <div style={{
                background: '#f8f9fa',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{ margin: '0 0 0.5rem 0' }}>⚙️ Atnaujinti statusą</h4>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <select
                    defaultValue={selectedRequest.prasymo_statusas}
                    id="status-select"
                    style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="naujas">Naujas</option>
                    <option value="peržiūrimas">Peržiūrimas</option>
                    <option value="atsakytas">Atsakytas</option>
                    <option value="uždarytas">Uždarytas</option>
                    <option value="atmestas">Atmestas</option>
                  </select>

                  <select
                    defaultValue={selectedRequest.prioritetas}
                    id="priority-select"
                    style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="žemas">Žemas</option>
                    <option value="vidutinis">Vidutinis</option>
                    <option value="aukštas">Aukštas</option>
                    <option value="skubus">Skubus</option>
                  </select>
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Atsakymas:
                  </label>
                  <textarea
                    id="response-textarea"
                    defaultValue={selectedRequest.atsakymas || ''}
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontFamily: 'inherit'
                    }}
                    placeholder="Įveskite atsakymą vartotojui..."
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  className="btn-outline"
                  onClick={() => setShowRequestModal(false)}
                  style={{ padding: '0.75rem 1.5rem' }}
                >
                  Atšaukti
                </button>
                <button
                  className="btn-cta"
                  onClick={() => {
                    const statusEl = document.getElementById('status-select') as HTMLSelectElement;
                    const priorityEl = document.getElementById('priority-select') as HTMLSelectElement;
                    const responseEl = document.getElementById('response-textarea') as HTMLTextAreaElement;

                    handleUpdateRequestStatus(selectedRequest.id, {
                      prasymo_statusas: statusEl?.value,
                      prioritetas: priorityEl?.value,
                      atsakymas: responseEl?.value
                    });
                  }}
                  style={{ padding: '0.75rem 1.5rem' }}
                >
                  💾 Išsaugoti
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminDashboardLayout>
  );
};

export default AdminDashboard;